import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { getArticleBySlug } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <i className="ri-emotion-sad-line text-6xl text-foreground-600 mb-4 block"></i>
            <h2 className="text-xl font-semibold text-foreground-200 mb-2">文章未找到</h2>
            <Link to="/" className="text-sm text-primary-400 hover:text-primary-300 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryMap: Record<string, string> = {
    'video-platforms': '视频平台',
    'live-streaming': '直播平台',
    'image-gallery': '图片社区',
    'forums': '社区论坛',
    'novels': '小说文学',
    'dating': '交友社交',
    'games': '游戏娱乐',
    'tools': '工具资源',
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    image: article.thumbnail,
    url: `https://adultnav.example.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '成人导航',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://adultnav.example.com' },
      { '@type': 'ListItem', position: 2, name: categoryMap[article.category] || '文章', item: `https://adultnav.example.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://adultnav.example.com/article/${article.slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 成人导航`}
        description={article.excerpt}
        keywords={`成人导航,${article.tags.join(',')},${categoryMap[article.category] || ''},成人内容`}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.thumbnail}
        schemaData={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-20">
          <article className="px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
                <Link
                  to={`/category/${article.category}`}
                  className="hover:text-primary-400 transition-colors cursor-pointer"
                >
                  {categoryMap[article.category] || '文章'}
                </Link>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
                <span className="text-foreground-300 line-clamp-1">{article.title}</span>
              </nav>

              <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-8">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/40 to-transparent"></div>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-background-800/60">
                <span className="text-xs text-foreground-600 flex items-center gap-1">
                  <i className="ri-user-line text-[10px]"></i> {article.author}
                </span>
                <span className="text-xs text-foreground-600 flex items-center gap-1">
                  <i className="ri-calendar-line text-[10px]"></i> {article.date}
                </span>
                <span className="text-xs text-foreground-600 flex items-center gap-1">
                  <i className="ri-time-line text-[10px]"></i> {article.readTime}
                </span>
                <span className="text-xs text-foreground-600 flex items-center gap-1">
                  <i className="ri-eye-line text-[10px]"></i> {article.views.toLocaleString()} 阅读
                </span>
                <div className="flex items-center gap-1.5 ml-auto">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-secondary-500/10 text-secondary-400 text-[10px] rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div
                className="prose prose-invert prose-sm md:prose-base max-w-none
                  prose-headings:text-foreground-100 prose-headings:font-heading
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-foreground-400 prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-foreground-200
                  prose-ul:text-foreground-400 prose-li:marker:text-primary-500
                  prose-a:text-primary-400 prose-a:no-underline hover:prose-a:text-primary-300
                  [&_ul]:space-y-1 [&_ul]:mb-4 [&_ul]:pl-5
                  [&_li]:text-sm [&_li]:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-800/60">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-200 mb-1">这篇文章对您有帮助吗？</h4>
                    <p className="text-xs text-foreground-500">分享给更多朋友，发现优质成人内容平台</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="w-9 h-9 rounded-lg bg-background-800 hover:bg-primary-500/20 flex items-center justify-center text-foreground-400 hover:text-primary-400 transition-all duration-200 cursor-pointer">
                      <i className="ri-thumb-up-line text-sm"></i>
                    </button>
                    <button className="w-9 h-9 rounded-lg bg-background-800 hover:bg-primary-500/20 flex items-center justify-center text-foreground-400 hover:text-primary-400 transition-all duration-200 cursor-pointer">
                      <i className="ri-share-forward-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}